import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export type PRDNodeData = {
  type: 'solution' | 'deliverable' | 'prd';
  id: number;
  label: string;
  description: string;
  jiraKey?: string;
};

export type PRDNode = {
  id: string;
  type: 'prd';
  data: PRDNodeData;
};

export type PRDEdge = [string, string];

export type PRDReport = {
  title: string;
  subtitle: string;
  nodes: PRDNode[];
  edges: PRDEdge[];
};

type UsePRDReportResult = {
  data: PRDReport | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
};


/**
 * Development mode data model (FK chain, no link tables):
 *   Solution → Deliverable → PRD → Project
 *
 * - **Deliverable**: minimal acceptable set of solutions that creates noticeable
 *   value for stakeholders. Usually indivisible — splitting further would make it
 *   too small to produce meaningful value. Linked to a Jira epic via jira_key.
 * - **PRD**: a set of interrelated deliverables. Each deliverable creates its own
 *   value, but delivered together within one PRD they create synergies.
 *
 * Solution IDs come from the caller (derived from PSPS data that's already loaded),
 * avoiding duplicate PSPS chain traversal.
 */

async function fetchPRDGraph(
  organisationId: number,
  projectId: number,
  solutionIds: number[],
): Promise<PRDReport | null> {
  const { data: project, error: projectErr } = await supabase
    .from('project')
    .select('project_id, name')
    .eq('organisation_id', organisationId)
    .eq('project_id', projectId)
    .maybeSingle();

  if (projectErr) throw projectErr;
  if (!project) return null;

  // Fetch PRDs for this project (table may not exist before migration)
  const { data: prds, error: prdErr } = await supabase
    .from('prd')
    .select('prd_id, name, description')
    .eq('project_id', projectId)
    .eq('organisation_id', organisationId);

  // Table may not exist before migration — treat PRD/deliverable layers as empty
  const prdsData = prdErr ? [] : (prds ?? []);
  const prdIds = prdsData.map((p) => p.prd_id);

  // Fetch deliverables linked to those PRDs
  type DelRow = {
    deliverable_id: number;
    name: string;
    description: string | null;
    jira_key: string | null;
    prd_id: number | null;
  };

  let deliverables: DelRow[] = [];
  if (prdIds.length > 0) {
    const { data: delData, error: delErr } = await supabase
      .from('deliverable')
      .select('deliverable_id, name, description, jira_key, prd_id')
      .in('prd_id', prdIds)
      .eq('organisation_id', organisationId);

    if (!delErr) deliverables = (delData ?? []) as DelRow[];
  }

  // Fetch solution details for provided IDs
  type SolRow = {
    solution_id: number;
    name: string;
    description: string | null;
    deliverable_id: number | null;
  };

  let solutions: SolRow[] = [];
  if (solutionIds.length > 0) {
    // Try with deliverable_id first; fall back without it (column may not exist before migration)
    let { data: solData, error: solErr } = await supabase
      .from('solution')
      .select('solution_id, name, description, deliverable_id')
      .in('solution_id', solutionIds)
      .eq('organisation_id', organisationId);

    if (solErr) {
      const fallback = await supabase
        .from('solution')
        .select('solution_id, name, description')
        .in('solution_id', solutionIds)
        .eq('organisation_id', organisationId);
      if (!fallback.error) {
        solData = fallback.data?.map((s) => ({ ...s, deliverable_id: null })) ?? [];
        solErr = null;
      }
    }

    if (!solErr) solutions = (solData ?? []) as SolRow[];
  }

  // Fetch orphan deliverables referenced by solutions but not yet loaded (no PRD)
  const solDelIds = [...new Set(
    solutions.map((s) => s.deliverable_id).filter((id): id is number => id != null),
  )];
  const missingDelIds = solDelIds.filter((id) => !deliverables.some((d) => d.deliverable_id === id));

  if (missingDelIds.length > 0) {
    const { data: orphanDels, error: orphanErr } = await supabase
      .from('deliverable')
      .select('deliverable_id, name, description, jira_key, prd_id')
      .in('deliverable_id', missingDelIds)
      .eq('organisation_id', organisationId);

    if (!orphanErr && orphanDels) {
      deliverables = [...deliverables, ...(orphanDels as DelRow[])];
    }
  }

  const delIds = deliverables.map((d) => d.deliverable_id);

  // Count deliverables per PRD for single-deliverable collapse
  const delCountByPrd: Record<number, number> = {};
  for (const d of deliverables) {
    if (d.prd_id != null) {
      delCountByPrd[d.prd_id] = (delCountByPrd[d.prd_id] ?? 0) + 1;
    }
  }

  // Build nodes and edges
  const nodes: PRDNode[] = [];
  const edges: PRDEdge[] = [];
  const seen = new Set<string>();

  function addNode(
    nid: string,
    ntype: PRDNodeData['type'],
    entity: Record<string, unknown>,
    pk: string,
    extra?: Partial<PRDNodeData>
  ) {
    if (seen.has(nid)) return;
    seen.add(nid);
    const data: PRDNodeData = {
      type: ntype,
      id: entity[pk] as number,
      label: (entity.name as string) ?? '',
      description: (entity.description as string) ?? '',
      ...extra,
    };
    nodes.push({ id: nid, type: 'prd', data });
  }

  // Add PRD nodes
  for (const prd of prdsData) {
    addNode(`prd${prd.prd_id}`, 'prd', prd, 'prd_id');
  }

  // Add deliverable nodes (skip if single-deliverable collapse)
  for (const d of deliverables) {
    const single = delCountByPrd[d.prd_id ?? 0] === 1;
    const delNid = `del${d.deliverable_id}`;
    const prdNid = d.prd_id != null ? `prd${d.prd_id}` : null;

    if (!single) {
      const extra: Partial<PRDNodeData> = d.jira_key ? { jiraKey: d.jira_key } : {};
      addNode(delNid, 'deliverable', d as unknown as Record<string, unknown>, 'deliverable_id', extra);
      if (prdNid) edges.push([delNid, prdNid]);
    }

    // For single-deliverable PRDs, pass jira_key up to PRD node
    if (single && prdNid && d.jira_key) {
      const prdNode = nodes.find((n) => n.id === prdNid);
      if (prdNode) prdNode.data.jiraKey = d.jira_key;
    }
  }

  // Add solution nodes and edges
  for (const sol of solutions) {
    const solNid = `sol${sol.solution_id}`;
    addNode(solNid, 'solution', sol as unknown as Record<string, unknown>, 'solution_id');

    // Only add edges for solutions that are assigned to a deliverable
    if (sol.deliverable_id != null && delIds.includes(sol.deliverable_id)) {
      const del = deliverables.find((d) => d.deliverable_id === sol.deliverable_id);
      const prdIdForDel = del?.prd_id;
      const single = delCountByPrd[prdIdForDel ?? 0] === 1;

      if (single && prdIdForDel != null) {
        edges.push([solNid, `prd${prdIdForDel}`]);
      } else {
        edges.push([solNid, `del${sol.deliverable_id}`]);
      }
    }
    // Unassigned solutions appear as standalone nodes (no edges)
  }

  // Deduplicate edges
  const edgeSet = new Set<string>();
  const uniqueEdges: PRDEdge[] = [];
  for (const edge of edges) {
    const key = `${edge[0]}-${edge[1]}`;
    if (!edgeSet.has(key)) {
      edgeSet.add(key);
      uniqueEdges.push(edge);
    }
  }

  const nSolutions = nodes.filter((n) => n.data.type === 'solution').length;
  const nDeliverables = nodes.filter((n) => n.data.type === 'deliverable').length;
  const nPrds = nodes.filter((n) => n.data.type === 'prd').length;

  return {
    title: project.name ?? '',
    subtitle: `${nSolutions} SOLUTION${nSolutions !== 1 ? 'S' : ''} · ${nDeliverables} DELIVERABLE${nDeliverables !== 1 ? 'S' : ''} · ${nPrds} PRD${nPrds !== 1 ? 'S' : ''}`,
    nodes,
    edges: uniqueEdges,
  };
}

/**
 * Fetches the PRD graph for Development mode.
 * Solution IDs are passed in from the PSPS data (already loaded in Research mode),
 * so this hook doesn't need to re-traverse the PSPS chain.
 */
export function usePRDReport(
  organisationId: number | null,
  projectId: number | null,
  solutionIds: number[],
): UsePRDReportResult {
  const [data, setData] = useState<PRDReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    if (organisationId == null || projectId == null) {
      setData(null);
      return;
    }
    setIsLoading(true);
    setError(null);

    try {
      const result = await fetchPRDGraph(organisationId, projectId, solutionIds);
      setData(result);
    } catch (err) {
      setError(err as Error);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [organisationId, projectId, solutionIds]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, isLoading, error, refetch };
}
